// ABOUTME: Read aloud — an agent message's own action reads it in the business's chosen voice
// (Settings › Agent voice) through the streaming POST /agent/speech endpoint; one message at a
// time, and the same action stops it. Plays via MediaSource where supported so playback starts
// from the first audio chunk rather than waiting for the whole clip; falls back to a buffered
// Blob (Safari lacks MSE mp3 support).

import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchAgentSpeech } from '@/lib/agentSpeech'
import { useBusinessSettingsStore } from '@/stores/useBusinessSettingsStore'

const MP3_MIME = 'audio/mpeg'

function canStreamPlayback(): boolean {
  return typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported(MP3_MIME)
}

async function fetchSpeechStream(
  text: string,
  signal: AbortSignal
): Promise<ReadableStream<Uint8Array>> {
  // The business's chosen voice (Settings › voice picker, #2609 Part 4);
  // undefined lets the backend fall back to its own stored default.
  const voiceId = useBusinessSettingsStore.getState().settings?.agent_voice_id
  const response = await fetchAgentSpeech(text, voiceId, signal)
  if (!response.body) {
    throw new Error('Read aloud response had no body')
  }
  return response.body
}

async function waitForBufferReady(sourceBuffer: SourceBuffer): Promise<void> {
  if (!sourceBuffer.updating) return
  await new Promise<void>((resolve) =>
    sourceBuffer.addEventListener('updateend', () => resolve(), { once: true })
  )
}

/** Pump a fetch body into a MediaSource SourceBuffer, starting playback on
 *  the first chunk instead of waiting for the whole stream to buffer. */
async function playViaMediaSource(
  body: ReadableStream<Uint8Array>,
  audio: HTMLAudioElement,
  onFirstChunk: () => void
): Promise<void> {
  const mediaSource = new MediaSource()
  audio.src = URL.createObjectURL(mediaSource)
  await new Promise<void>((resolve) =>
    mediaSource.addEventListener('sourceopen', () => resolve(), { once: true })
  )
  const sourceBuffer = mediaSource.addSourceBuffer(MP3_MIME)
  const reader = body.getReader()
  let firstChunk = true

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    if (!value || value.length === 0) continue
    await waitForBufferReady(sourceBuffer)
    sourceBuffer.appendBuffer(value as BufferSource)
    if (firstChunk) {
      firstChunk = false
      onFirstChunk()
      void audio.play()
    }
  }
  await waitForBufferReady(sourceBuffer)
  if (mediaSource.readyState === 'open') mediaSource.endOfStream()
}

/** Buffer the whole stream into a Blob before playing — the MSE fallback. */
async function playViaBlob(
  body: ReadableStream<Uint8Array>,
  audio: HTMLAudioElement,
  onReady: () => void
): Promise<void> {
  const reader = body.getReader()
  const chunks: Uint8Array[] = []
  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    if (value) chunks.push(value)
  }
  audio.src = URL.createObjectURL(new Blob(chunks as BlobPart[], { type: MP3_MIME }))
  onReady()
  void audio.play()
}

export function useReadAloud() {
  const [speakingId, setSpeakingId] = useState<string | null>(null)
  const [loadingId, setLoadingId] = useState<string | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const stop = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
    if (audioRef.current) {
      audioRef.current.pause()
      audioRef.current.removeAttribute('src')
      audioRef.current.load()
      audioRef.current = null
    }
    setSpeakingId(null)
    setLoadingId(null)
  }, [])

  const play = useCallback(
    async (messageId: string, text: string) => {
      if (!text.trim()) return
      stop()
      const controller = new AbortController()
      abortRef.current = controller
      setLoadingId(messageId)

      try {
        const body = await fetchSpeechStream(text, controller.signal)
        const audio = new Audio()
        audioRef.current = audio
        const markDone = () => setSpeakingId((id) => (id === messageId ? null : id))
        audio.addEventListener('ended', markDone)
        audio.addEventListener('error', markDone)
        const markPlaying = () => {
          setLoadingId(null)
          setSpeakingId(messageId)
        }

        if (canStreamPlayback()) {
          await playViaMediaSource(body, audio, markPlaying)
        } else {
          await playViaBlob(body, audio, markPlaying)
        }
      } catch (err) {
        if ((err as Error)?.name !== 'AbortError') {
          console.error('Read aloud failed', err)
        }
        setLoadingId(null)
        setSpeakingId((id) => (id === messageId ? null : id))
      }
    },
    [stop]
  )

  useEffect(() => stop, [stop])

  return { speakingId, loadingId, play, stop }
}
